import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useRefreshAuthState } from 'platform/components/ReauthContext';
import { DevError } from 'platform/Error';
import { getLoginUrl } from 'platform/loginRedirect';
import navigationHelpers from 'platform/navigationHelpers';
import type { Observability } from 'platform/observability';
import { useObservability } from 'platform/observability';

const ONE_HOUR = 1000 * 60 * 60;
const ONE_DAY = ONE_HOUR * 24;
/** @internal */
export const STALE_PAGE_WINDOW = ONE_DAY;
/** @internal */
export const STALE_AUTH_WINDOW = ONE_HOUR * 2;

/** @internal */
export const PageStalenessTimerContext = createContext<{
  /**
   * The time the page first loaded (in ms since epoch)
   */
  pageLoadStartTime: number;
  /**
   * The time the customer's auth was last validated (in ms since epoch)
   */
  authValidatedTime: number;
  /**
   * Helper function to determine if the page is stale
   */
  isPageStale: () => boolean;
  /**
   * Helper function to determine if the auth is stale
   */
  isAuthStale: () => boolean;
}>({
  pageLoadStartTime: Date.now(),
  authValidatedTime: Date.now(),
  isPageStale: () => false,
  isAuthStale: () => false,
});

/**
 * Handler for when the page enters/exits the browser foreground
 * @internal
 */
export const onVisibilityChange = (
  isPageInBackground: boolean,
  pageEnteredBackgroundTime: number | undefined,
  pageLoadStartTime: number,
  setPageEnteredBackgroundTime: (time: number | undefined) => void,
  trackEvent: Observability['trackEvent'],
) => {
  if (isPageInBackground) {
    // Page has entered the background
    setPageEnteredBackgroundTime(Date.now());
  } else {
    // Page has re-entered the foreground
    if (pageEnteredBackgroundTime && isPageStale(pageEnteredBackgroundTime)) {
      // If the page has been open for more than 1 day in the background, hard-reload the page
      trackEvent(
        'stale-page-reload',
        'page-reentered-foreground',
        { timeSincePageFirstLoadedMs: Date.now() - pageLoadStartTime },
        // Adds a custom datadog time event in the user's session
        { addTiming: true },
      );
      navigationHelpers.hardPageReload();
    }

    // Reset background timer if page wasn't reloaded
    setPageEnteredBackgroundTime(undefined);
  }
};

/**
 * Returns true if the page should be reloaded due to staleness
 * @throws Error if startTime is not provided
 * @internal
 */
export const isPageStale = (startTime: number) => {
  const timeSincePageFirstLoaded = Date.now() - startTime;
  return timeSincePageFirstLoaded > STALE_PAGE_WINDOW;
};

/**
 * Instructs the app to reload a customer's page in cake if it has been stale for at least 1 days. The page will not immediately reload, but will instead wait for the next customer navigation and conduct a hard reload rather than a soft reload.
 *
 * This is intended to reduce the number of old cake errors. This is usually due to customers having old tabs open or browser cached code that triggers queries against code that's no longer in production.
 */
export const PageStalenessTimeProvider: FC<PropsWithChildren> = (props) => {
  const [pageLoadStartTime] = useState<number>(() => Date.now());
  const [authValidatedTime] = useState<number>(() => Date.now());
  const [pageEnteredBackgroundTime, setPageEnteredBackgroundTime] = useState<number | undefined>();
  const { trackEvent } = useObservability();

  const visibilityChangeHandler = () =>
    onVisibilityChange(
      document.visibilityState === 'hidden',
      pageEnteredBackgroundTime,
      pageLoadStartTime,
      setPageEnteredBackgroundTime,
      trackEvent,
    );

  useEffect(() => {
    document.addEventListener('visibilitychange', visibilityChangeHandler);
    return () => {
      // Cleanup event listener
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only trigger on certain timing deps changes
  }, [pageEnteredBackgroundTime, pageLoadStartTime]);

  const context = useMemo(
    () => ({
      pageLoadStartTime,
      authValidatedTime,
      // Contextual function defaults to page load time if the page has never entered the background
      isPageStale: () => isPageStale(pageLoadStartTime),
      // Contextual function defaults to auth validated time if the page has never entered the background
      isAuthStale: () => isAuthStale(authValidatedTime),
    }),
    [authValidatedTime, pageLoadStartTime],
  );

  return <PageStalenessTimerContext.Provider value={context} {...props} />;
};

/**
 * Returns true if the auth should be re-validated due to staleness
 * @throws Error if startTime is not provided
 * @internal
 */
export const isAuthStale = (startTime: number) => {
  const TimeSinceAuthValidated = Date.now() - startTime;
  return TimeSinceAuthValidated > STALE_AUTH_WINDOW;
};

/**
 * Hook to access the PageStalenessTimerContext
 * @throws Error if the consuming code is wrapped with the correct provider
 */
export const usePageStalenessTimer = () => {
  const value = useContext(PageStalenessTimerContext);
  if (!value) {
    throw new DevError('PageStalenessTimerContext missing');
  }
  return value;
};

/**
 * Hook to check if the page or auth state is stale
 *
 * @returns true if navigation was triggered, false otherwise
 */
export const useStalePageCheck = () => {
  const { trackEvent } = useObservability();
  const { pageLoadStartTime, isPageStale, isAuthStale } = usePageStalenessTimer();
  const refreshAuthState = useRefreshAuthState();

  return useCallback(async () => {
    // If the customer's authentication hasn't been re-validated in the last 2 hours, refresh it
    if (isAuthStale()) {
      try {
        await refreshAuthState();
      } catch {
        navigationHelpers.hardNavAssign(getLoginUrl(window.location.href));
        return true;
      }
    }

    // If the page has been open for more than 1 day, hard-reload the page
    if (isPageStale()) {
      trackEvent(
        'stale-page-reload',
        'first-navigation-after-window',
        { timeSincePageFirstLoadedMs: Date.now() - pageLoadStartTime },
        // Adds a custom datadog time event in the user's session
        { addTiming: true },
      );

      // This is an intentional hard redirect to reset the staleness of the page
      navigationHelpers.hardPageReload();
      return true;
    }

    return false;
  }, [isAuthStale, isPageStale, pageLoadStartTime, refreshAuthState, trackEvent]);
};
